/**
 * StrategyPanel — TradingScript strategy editor + backtesting results panel.
 *
 * Combines:
 * - A syntax-highlighted `<textarea>` editor (overlay technique for colours)
 * - Template picker (Golden Cross, RSI Oversold, Bollinger Breakout, etc.)
 * - Backtesting engine runner (`runBacktest`) with capital / commission config
 * - Three-tab results view: Editor, Summary stats, Trades ledger
 *
 * Parse errors are shown inline below the editor in real time.
 * Backtest runs asynchronously via `setTimeout` to avoid blocking the UI.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  parseStrategy,
  highlightTradescript,
  STRATEGY_TEMPLATES,
} from "../../service/tradescript-parser";
import { runBacktest, BacktestResult, BacktestConfig, Trade } from "../../service/backtest-engine";
import { CandleData } from "../../types/chart.types";
import "./StrategyPanel.scss";

interface StrategyPanelProps {
  candles: CandleData[];
  symbol: string;
  timeframe: string;
  colorTheme: string;
  onClose: () => void;
}

type Tab = "editor" | "results" | "trades";

const StrategyPanel: React.FC<StrategyPanelProps> = ({
  candles,
  symbol,
  timeframe,
  colorTheme,
  onClose,
}) => {
  const [code, setCode] = useState(STRATEGY_TEMPLATES["Golden Cross"]);
  const [activeTab, setActiveTab] = useState<Tab>("editor");
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("Golden Cross");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);

  // Config
  const [initialCapital, setInitialCapital] = useState("100000");
  const [commissionPct, setCommissionPct] = useState("0.03");

  // Parse errors for inline display
  const parseErrors = useMemo(() => {
    const parsed = parseStrategy(code);
    return parsed.errors;
  }, [code]);

  // Sync scroll between textarea and highlight overlay
  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleRunBacktest = useCallback(() => {
    if (candles.length < 50) return;
    setIsRunning(true);

    // Run async-like using setTimeout to not block UI
    setTimeout(() => {
      try {
        const config: Partial<BacktestConfig> = {
          initialCapital: parseFloat(initialCapital) || 100000,
          commissionPercent: parseFloat(commissionPct) || 0.03,
        };
        const res = runBacktest(code, candles, config);
        setResult(res);
        setActiveTab("results");
      } catch (e) {
        console.error("[Backtest] Error:", e);
      } finally {
        setIsRunning(false);
      }
    }, 50);
  }, [code, candles, initialCapital, commissionPct]);

  const handleTemplateChange = useCallback((name: string) => {
    if (STRATEGY_TEMPLATES[name]) {
      setCode(STRATEGY_TEMPLATES[name]);
      setSelectedTemplate(name);
    }
  }, []);

  const handleSaveStrategy = useCallback(() => {
    try {
      const strategies = JSON.parse(localStorage.getItem("marketview_strategies") || "{}");
      const name = prompt("Strategy name:", `My Strategy ${Object.keys(strategies).length + 1}`);
      if (!name) return;
      strategies[name] = code;
      localStorage.setItem("marketview_strategies", JSON.stringify(strategies));
    } catch {}
  }, [code]);

  const handleLoadStrategy = useCallback(() => {
    try {
      const strategies = JSON.parse(localStorage.getItem("marketview_strategies") || "{}");
      const names = Object.keys(strategies);
      if (names.length === 0) { alert("No saved strategies"); return; }
      const name = prompt(`Load strategy:\n${names.join("\n")}`);
      if (name && strategies[name]) {
        setCode(strategies[name]);
      }
    } catch {}
  }, []);

  const formatCurrency = (n: number) =>
    "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatPercent = (n: number) =>
    n.toFixed(2) + "%";

  return (
    <div className={`strategy-panel ${colorTheme}`}>
      <div className="strategy-header">
        <div className="strategy-tabs">
          <button
            className={`strategy-tab ${activeTab === "editor" ? "active" : ""}`}
            onClick={() => setActiveTab("editor")}
          >
            Editor
          </button>
          <button
            className={`strategy-tab ${activeTab === "results" ? "active" : ""}`}
            onClick={() => setActiveTab("results")}
            disabled={!result}
          >
            Results
          </button>
          <button
            className={`strategy-tab ${activeTab === "trades" ? "active" : ""}`}
            onClick={() => setActiveTab("trades")}
            disabled={!result}
          >
            Trades ({result?.totalTrades || 0})
          </button>
        </div>
        <div className="strategy-header-actions">
          <span className="strategy-symbol-badge">{symbol} • {timeframe}</span>
          <button className="strategy-close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {activeTab === "editor" && (
        <div className="strategy-editor-tab">
          <div className="strategy-toolbar">
            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="template-select"
            >
              {Object.keys(STRATEGY_TEMPLATES).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button className="strategy-btn" onClick={handleSaveStrategy} title="Save">💾 Save</button>
            <button className="strategy-btn" onClick={handleLoadStrategy} title="Load">📂 Load</button>
            <div className="strategy-spacer" />
            <div className="strategy-config">
              <label>Capital: ₹
                <input
                  type="number"
                  value={initialCapital}
                  onChange={(e) => setInitialCapital(e.target.value)}
                  className="config-input"
                />
              </label>
              <label>Commission:
                <input
                  type="number"
                  value={commissionPct}
                  onChange={(e) => setCommissionPct(e.target.value)}
                  step="0.01"
                  className="config-input config-input-sm"
                />%
              </label>
            </div>
            <button
              className="strategy-run-btn"
              onClick={handleRunBacktest}
              disabled={isRunning || candles.length < 50}
            >
              {isRunning ? "Running..." : "▶ Run Backtest"}
            </button>
          </div>

          <div className="code-editor-wrapper">
            <div className="line-numbers">
              {code.split("\n").map((_, i) => (
                <div
                  key={i}
                  className={`line-num ${parseErrors.some(e => e.line === i + 1) ? "error" : ""}`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
            <div className="code-editor-area">
              <pre
                ref={highlightRef}
                className="code-highlight"
                dangerouslySetInnerHTML={{ __html: highlightTradescript(code) + "\n" }}
              />
              <textarea
                ref={textareaRef}
                className="code-textarea"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onScroll={handleScroll}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>
          </div>

          {parseErrors.length > 0 && (
            <div className="parse-errors">
              {parseErrors.map((err, i) => (
                <div key={i} className="parse-error">
                  Line {err.line}: {err.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "results" && result && (
        <div className="strategy-results-tab">
          <div className="results-summary">
            <div className="result-card">
              <span className="result-label">Total Return</span>
              <span className={`result-value ${result.totalReturn >= 0 ? "positive" : "negative"}`}>
                {formatCurrency(result.totalReturn)}
              </span>
              <span className={`result-pct ${result.totalReturnPercent >= 0 ? "positive" : "negative"}`}>
                {formatPercent(result.totalReturnPercent)}
              </span>
            </div>
            <div className="result-card">
              <span className="result-label">Final Capital</span>
              <span className="result-value">{formatCurrency(result.finalCapital)}</span>
            </div>
            <div className="result-card">
              <span className="result-label">Win Rate</span>
              <span className="result-value">{formatPercent(result.winRate)}</span>
            </div>
            <div className="result-card">
              <span className="result-label">Sharpe Ratio</span>
              <span className="result-value">{result.sharpeRatio.toFixed(3)}</span>
            </div>
          </div>

          <div className="results-grid">
            <div className="result-row">
              <span>Total Trades</span>
              <span>{result.totalTrades}</span>
            </div>
            <div className="result-row">
              <span>Winning</span>
              <span className="positive">{result.winningTrades}</span>
            </div>
            <div className="result-row">
              <span>Losing</span>
              <span className="negative">{result.losingTrades}</span>
            </div>
            <div className="result-row">
              <span>Avg Win</span>
              <span className="positive">{formatCurrency(result.avgWin)}</span>
            </div>
            <div className="result-row">
              <span>Avg Loss</span>
              <span className="negative">{formatCurrency(result.avgLoss)}</span>
            </div>
            <div className="result-row">
              <span>Profit Factor</span>
              <span>{result.profitFactor === Infinity ? "∞" : result.profitFactor.toFixed(2)}</span>
            </div>
            <div className="result-row">
              <span>Max Drawdown</span>
              <span className="negative">
                {formatCurrency(result.maxDrawdown)} ({formatPercent(result.maxDrawdownPercent)})
              </span>
            </div>
            <div className="result-row">
              <span>Max Consecutive Wins</span>
              <span>{result.maxConsecutiveWins}</span>
            </div>
            <div className="result-row">
              <span>Max Consecutive Losses</span>
              <span>{result.maxConsecutiveLosses}</span>
            </div>
          </div>

          {/* Mini equity curve */}
          {result.equityCurve.length > 0 && (
            <div className="equity-curve-section">
              <h4>Equity Curve</h4>
              <EquityCurve
                data={result.equityCurve}
                initialCapital={result.initialCapital}
                colorTheme={colorTheme}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === "trades" && result && (
        <div className="strategy-trades-tab">
          <div className="trades-table-wrap">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Entry Time</th>
                  <th>Entry Price</th>
                  <th>Exit Time</th>
                  <th>Exit Price</th>
                  <th>P&L</th>
                  <th>P&L %</th>
                  <th>Bars</th>
                </tr>
              </thead>
              <tbody>
                {result.trades.map((trade, i) => (
                  <tr key={i} className={(trade.pnl || 0) >= 0 ? "win" : "loss"}>
                    <td>{i + 1}</td>
                    <td>{formatTradeTime(trade.entryTime)}</td>
                    <td>{trade.entryPrice.toFixed(2)}</td>
                    <td>{trade.exitTime ? formatTradeTime(trade.exitTime) : "Open"}</td>
                    <td>{trade.exitPrice?.toFixed(2) || "—"}</td>
                    <td className={(trade.pnl || 0) >= 0 ? "positive" : "negative"}>
                      {formatCurrency(trade.pnl || 0)}
                    </td>
                    <td className={(trade.pnlPercent || 0) >= 0 ? "positive" : "negative"}>
                      {formatPercent(trade.pnlPercent || 0)}
                    </td>
                    <td>{trade.holdingBars || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function formatTradeTime(unixTime: number): string {
  const d = new Date(unixTime * 1000);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Mini Equity Curve Canvas ───────────────────────────────────

interface EquityCurveProps {
  data: { time: number; equity: number }[];
  initialCapital: number;
  colorTheme: string;
}

const EquityCurve: React.FC<EquityCurveProps> = ({ data, initialCapital, colorTheme }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    const padding = { top: 10, bottom: 20, left: 10, right: 10 };

    ctx.clearRect(0, 0, w, h);

    const equities = data.map(d => d.equity);
    const minEq = Math.min(...equities);
    const maxEq = Math.max(...equities);
    const range = maxEq - minEq || 1;

    const xScale = (w - padding.left - padding.right) / (data.length - 1 || 1);
    const yScale = (h - padding.top - padding.bottom) / range;

    // Draw baseline (initial capital)
    const baselineY = h - padding.bottom - (initialCapital - minEq) * yScale;
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = colorTheme === "dark" ? "#555" : "#ccc";
    ctx.moveTo(padding.left, baselineY);
    ctx.lineTo(w - padding.right, baselineY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw equity curve
    ctx.beginPath();
    ctx.strokeStyle = "#2962ff";
    ctx.lineWidth = 1.5;

    for (let i = 0; i < data.length; i++) {
      const x = padding.left + i * xScale;
      const y = h - padding.bottom - (data[i].equity - minEq) * yScale;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Fill under curve
    const lastX = padding.left + (data.length - 1) * xScale;
    const lastY = h - padding.bottom - (data[data.length - 1].equity - minEq) * yScale;
    ctx.lineTo(lastX, h - padding.bottom);
    ctx.lineTo(padding.left, h - padding.bottom);
    ctx.closePath();
    ctx.fillStyle = data[data.length - 1].equity >= initialCapital
      ? "rgba(41, 98, 255, 0.08)"
      : "rgba(239, 83, 80, 0.08)";
    ctx.fill();
  }, [data, initialCapital, colorTheme]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={120}
      className="equity-canvas"
    />
  );
};

export default StrategyPanel;
